<script>
  import CloseButton from "./CloseButton.svelte";
  import { fly, fade } from "svelte/transition";
  import { onMount } from "svelte";
  import { tick } from "svelte";
  import { onDestroy } from "svelte";
  import { tourFunctions } from "../tour-functions";

  export let embedCode;
  export let delay;
  export let redisplay;
  export let title = "";
  let contentInner;

  let visible = delay ? false : true;
  function closePopup() {
    visible = false;
    tourFunctions.focus();
    if (redisplay && !userInteracted) {
      setTimeout(async () => {
        visible = true;
        await tick();
        loadEmbedCode();
      }, redisplay);
    }
  }

  let interactivityEvents = ["click", "keydown"];
  let cookieName = "userInteracted";
  let cookieExpiryDays = 30;
  let userInteracted = false;

  function setCookie(name, value, days) {
    let expires = "";
    if (days) {
      let date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  function getCookie(name) {
    let nameEQ = name + "=";
    let ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  function registerInteractions() {
    interactivityEvents.forEach((event) => {
      contentInner.addEventListener(event, handleInteraction);
    });
  }

  function unregisterInteractions() {
    interactivityEvents.forEach((event) => {
      contentInner.removeEventListener(event, handleInteraction);
    });
  }

  function handleInteraction() {
    userInteracted = true;
    setCookie(cookieName, true, cookieExpiryDays);
    unregisterInteractions();
  }

  function loadEmbedCode() {
    const parser = new DOMParser();
    const doc = parser.parseFromString(embedCode, "text/html");
    const embedContainer = document.getElementById("form-embed");

    doc.body.childNodes.forEach((node) => {
      if (node.nodeName.toLowerCase() === "script") {
        const script = document.createElement("script");

        // If external script
        if (node.src) {
          script.src = node.src;
        } else {
          // Inline script
          script.textContent = node.textContent;
        }

        // Copy all other attributes
        Array.from(node.attributes).forEach((attr) => {
          if (attr.name !== "src") {
            script.setAttribute(attr.name, attr.value);
          }
        });

        embedContainer.appendChild(script);
      } else {
        const newNode = document.importNode(node, true);
        embedContainer.appendChild(newNode);
      }
    });
  }

  onMount(() => {
    if (redisplay && getCookie(cookieName)) {
      return;
    }

    setTimeout(async () => {
      visible = true;
      await tick();
      loadEmbedCode();
      registerInteractions();
    }, delay);
  });

  onDestroy(() => {
    unregisterInteractions();
  });
</script>

{#if visible}
  <div class="popup-modal">
    <div class="popup-area">
      <div class="popup-window-wrap">
        <div class="popup-window" transition:fly={{ y: 1800, duration: 500 }}>
          <div class="popup-content">
            <div class="popup">
              <CloseButton close={closePopup} />

              <div class="content column">
                <h2 class="title">{title}</h2>
                <div class="content-inner" bind:this={contentInner}>
                  <div id="form-embed"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="popup-bg"
        on:click={closePopup}
        transition:fade={{ duration: 500 }}
      ></div>
    </div>
  </div>
{/if}

<style>
  .popup {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
  }

  .column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
    height: 100%;
  }

  .title {
    margin-bottom: 1em;
    margin-left: 1em;
    font-family: var(--font-heading), sans-serif;
    font-size: 2.5rem;
    line-height: 1;
  }

  .content {
    justify-content: center;
    font-family: var(--font-body), sans-serif;
    line-height: 1.5;
  }

  .content-inner {
    padding: 2rem;
    padding-top: 0;
    overflow-y: scroll;
    height: fit-content;
    max-height: 85vh;
    box-sizing: border-box;
    padding-bottom: 100px;
  }

  :global(.popup .content-inner a) {
    color: #fff;
    font-weight: 700;
  }

  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background: #fff;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #ddd;
  }

  .popup-area {
    position: fixed;
    z-index: 92001;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100vw;
    height: 100vh;
  }

  .popup-bg {
    position: fixed;
    z-index: 92000;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.9);
  }

  .popup-window {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99999;
    max-width: 100%;
    max-height: 100%;
  }

  .popup-window-wrap {
    position: relative;
    margin: 2rem;
    max-height: 100%;
    box-sizing: border-box;
  }

  .popup-window {
    position: relative;
    width: 40rem;
    max-width: 100%;
    max-height: 100%;
    margin: 2rem auto;
    color: black;
    border-radius: 10px;
    background: var(--theme-color);
    color: rgb(255, 255, 255);
    width: fit-content;
    width: 80vw;
    overflow: hidden;
  }

  @media only screen and (max-width: 600px) {
    .popup-area {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;

      width: 90%;
      height: 90%;
    }

    .popup-window-wrap,
    .popup-window {
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .popup-window {
      border-radius: none;
    }

    .column {
      height: 50%;
      width: 100%;
      flex: none;
    }

    .content.column {
      height: 50%;
    }

    .content {
      justify-content: flex-start;
    }

    .title {
      font-size: 1.75em;
    }

    .content-inner {
      font-size: 14px;
    }

    .popup-modal:not(.has-image) .content {
      margin-top: 40px;
    }
  }

  /* Form Styles */

  :global(#form-embed h2) {
    font-size: 2em;
    font-family: var(--font-heading), sans-serif;
  }

  :global(#form-embed .form_question) {
    padding: 0 0.5em;
  }

  :global(#form-embed label) {
    margin-bottom: 0.5em;
    font-weight: 500;
  }

  :global(#form-embed input[type="text"]),
  :global(#form-embed input[type="email"]),
  :global(#form-embed input[type="tel"]),
  :global(#form-embed select),
  :global(#form-embed textarea) {
    width: 100%;
    padding: 12px 20px;
    margin-bottom: 15px;
    box-sizing: border-box;
    border: 2px solid #aaa;
    border-radius: 5px;
    font-size: 16px;
    font-family: var(--font-body), sans-serif;
    outline: none;
  }

  :global(#form-embed button),
  :global(#form-embed input[type="submit"]) {
    padding: 10px 20px;
    background-color: #000;
    color: #fff;
    border: none;
    border-radius: 5px;
    font-size: 18px;
    font-family: var(--font-body), sans-serif;
    cursor: pointer;
    transition: background-color 0.3s ease;
    outline: none;
  }

  :global(#form-embed button:hover),
  :global(#form-embed input[type="submit"]:hover) {
    background-color: #666;
  }

  :global(#form-embed textarea) {
    min-height: 150px;
  }

  :global(#form-embed input:focus),
  :global(#form-embed textarea:focus),
  :global(#form-embed select:focus) {
    border-color: #007bff;
  }
</style>
